"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./page.module.css";
import Link from "next/link";
import NextImage from "next/image";
import { RevealOnScroll } from "@/components/ui/RevealOnScroll";

const ARTICLES_PER_PAGE = 5;
const SUMMARY_ARTICLES_LIMIT = 5;

const formatDate = (date) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));

function ArticleItem({ blog, index }) {
  return (
    <RevealOnScroll
      as="article"
      animation="fade-up"
      delay={index * 80}
      className={styles.articleCard}
    >
      <Link
        className={`${styles.articleLink} ${blog.cover ? styles.articleLinkWithCover : ""}`}
        href={`/blog/${blog.slug}`}
      >
        {blog.cover && (
          <div className={styles.articleMedia}>
            <NextImage
              src={blog.cover}
              alt={`Illustration de l'article : ${blog.title}`}
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 768px) 50vw, 280px"
            />
          </div>
        )}
        <div className={styles.articleContent}>
          <time dateTime={blog.publishedAt}>
            {formatDate(blog.publishedAt)}
          </time>
          <h4>{blog.title}</h4>
          <p className={styles.articleDescription}>{blog.description}</p>
        </div>
      </Link>
    </RevealOnScroll>
  );
}

function BlogHeader({ articleCount }) {
  return (
    <div className={styles.sectionHeading}>
      <div>
        <span className={styles.sectionLabel}>À lire maintenant</span>
        <h2 id="articles">Nos derniers articles</h2>
      </div>
      <span className={styles.articleCount}>{articleCount} articles</span>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <nav className={styles.pagination} aria-label="Pagination des articles">
      <button
        type="button"
        className={styles.paginationArrow}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Page précédente"
      >
        <ChevronLeft size={18} />
      </button>
      <div className={styles.paginationPages}>
        {Array.from({ length: totalPages }, (_, index) => index + 1).map(
          (page) => (
            <button
              type="button"
              key={page}
              className={styles.paginationPage}
              data-active={page === currentPage}
              onClick={() => onPageChange(page)}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </button>
          ),
        )}
      </div>
      <button
        type="button"
        className={styles.paginationArrow}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Page suivante"
      >
        <ChevronRight size={18} />
      </button>
    </nav>
  );
}

function BlogSidebar({
  blogs,
  isSummaryExpanded,
  onSummaryToggle,
}) {
  const summaryBlogs = isSummaryExpanded
    ? blogs
    : blogs.slice(0, SUMMARY_ARTICLES_LIMIT);

  return (
    <aside className={styles.sidebar} aria-label="Sommaire du blog">
      <div className={styles.sidebarCard}>
        <h2>Articles</h2>
        <p className={styles.sidebarIntro}>
          Retrouvez les articles les plus récents.
        </p>
        <nav aria-label="Navigation dans les articles">
          {summaryBlogs.map((blog) => (
            <Link
              key={blog.slug}
              className={styles.summaryLink}
              href={`/blog/${blog.slug}`}
            >
              <span className={styles.summaryTitle}>{blog.title}</span>
              <time dateTime={blog.publishedAt}>
                {formatDate(blog.publishedAt)}
              </time>
            </Link>
          ))}
        </nav>
        {blogs.length > SUMMARY_ARTICLES_LIMIT && (
          <button
            type="button"
            className={styles.summaryMore}
            onClick={onSummaryToggle}
            aria-expanded={isSummaryExpanded}
          >
            {isSummaryExpanded ? "Voir moins" : "Voir plus"}
          </button>
        )}
      </div>
    </aside>
  );
}

export function BlogLists({ blogs }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const totalPages = Math.ceil(blogs.length / ARTICLES_PER_PAGE);
  const firstArticleIndex = (currentPage - 1) * ARTICLES_PER_PAGE;

  const visibleBlogs = blogs.slice(
    firstArticleIndex,
    firstArticleIndex + ARTICLES_PER_PAGE,
  );
  const goToPage = (page) => {
    setCurrentPage(page);
    document.getElementById("articles")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className={styles.contentSection} aria-label="Articles du blog">
      <div className={styles.contentGrid}>
        <div className={styles.articleColumn}>
          <BlogHeader articleCount={blogs.length} />

          <div className={styles.articles}>
            {visibleBlogs.map((blog, index) => (
              <ArticleItem key={blog.slug} blog={blog} index={index} />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>

        <BlogSidebar
          blogs={blogs}
          isSummaryExpanded={isSummaryExpanded}
          onSummaryToggle={() => setIsSummaryExpanded((expanded) => !expanded)}
        />
      </div>
    </section>
  );
}
